import * as React from "react";
import { Popover } from "radix-ui";
import { Check, Copy, Info } from "lucide-react";
import { Eyebrow, cn } from "@ozpanel/ui";
import type { SettingsField } from "./DataForm";

// FieldInfo — the per-setting technical reference, on demand.
//
// A settings form showed a label and one line of help, and threw away everything else the
// module already declares about the field: the DAEMON'S OWN directive name (what you would
// actually look up in the service's documentation, grep for in its config, or pass to
// postconf/doveconf), the default, whether the running value still IS the default, the full
// option set with what each one means, and which file the value is written to.
//
// That is the difference between a form that lets you change things and a form that lets you
// understand what you are changing. All of it was already on the wire.

function isUnset(v: unknown) {
  return v === null || v === undefined || v === "";
}

/** Server-resolved Choices carry meaning (detail/risk); a bare `options` list carries only
 *  the value. Normalising to one shape keeps the renderer from having to know which it got. */
function normalizeChoices(f: SettingsField): { value: string; detail?: string; risk?: string }[] {
  if (f.choices?.length) return f.choices.map((c) => ({ value: c.value, detail: c.detail, risk: c.risk }));
  return (f.options ?? []).map((o) => ({ value: o }));
}

export function FieldInfo({
  field,
  value,
  module,
  file,
  overrides,
}: {
  field: SettingsField;
  value: unknown;
  /** The service this directive belongs to, e.g. "postfix" — names the vocabulary. */
  module?: string;
  /** Where the value is written, when the module declares a raw config path. */
  file?: string;
  /** Per-service overrides of this parameter, service → value. */
  overrides?: Record<string, string>;
}) {
  const [copied, setCopied] = React.useState(false);
  const current = isUnset(value) ? null : String(value);
  const dflt = field.placeholder || null;
  // "Customised" is only meaningful when we know the default; saying "default" for a field
  // whose default we were never told would be a guess presented as a fact.
  const customised = current !== null && dflt !== null ? current !== dflt : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(field.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked (insecure context) — the value is still selectable by hand */
    }
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`About ${field.label}`}
          className="ml-1.5 inline-grid size-[15px] place-items-center rounded-full text-faint transition-colors hover:bg-surface2 hover:text-accent"
        >
          <Info size={12} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="left"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-[340px] rounded-[11px] border border-line bg-surface p-0 shadow-[0_18px_50px_-12px_rgba(0,0,0,.55)]"
        >
          <div className="border-b border-line px-3.5 py-3">
            <Eyebrow accent>{module ? `${module} directive` : "Directive"}</Eyebrow>
            <button
              type="button"
              onClick={copy}
              title="Copy directive name"
              className="mt-1.5 flex w-full items-center gap-2 rounded-md bg-bg2 px-2 py-1.5 text-left transition-colors hover:bg-surface2"
            >
              <code className="min-w-0 flex-1 truncate font-mono text-[12.5px] text-fg">{field.key}</code>
              {copied ? <Check size={12} className="shrink-0 text-ok" /> : <Copy size={12} className="shrink-0 text-faint" />}
            </button>
          </div>

          <dl className="divide-y divide-line text-[12.5px]">
            <InfoRow label="Type" value={<span className="font-mono">{field.type}</span>} />
            <InfoRow
              label="Current"
              value={
                current === null ? (
                  <span className="text-faint">not set</span>
                ) : (
                  <span className="font-mono break-all">{current}</span>
                )
              }
            />
            {dflt && (
              <InfoRow
                label="Default"
                value={
                  <span className="flex items-center justify-end gap-1.5">
                    <span className="font-mono break-all text-muted">{dflt}</span>
                    {customised === false && <span className="shrink-0 text-[10px] text-ok">in use</span>}
                    {customised === true && <span className="shrink-0 text-[10px] text-warn">overridden</span>}
                  </span>
                }
              />
            )}
          </dl>

          {overrides && Object.keys(overrides).length > 0 && (
            <div className="border-t border-line bg-warnsoft/40 px-3.5 py-3">
              <Eyebrow className="mb-2 text-warn">Per-service overrides</Eyebrow>
              <ul className="space-y-1">
                {Object.entries(overrides).map(([svc, v]) => (
                  <li key={svc} className="flex items-baseline justify-between gap-3 text-[12px]">
                    <code className="font-mono text-muted">{svc}</code>
                    <code className="font-mono text-warn">{v}</code>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
                The value above is the global one. These services use their own, so changing the
                global will not affect them.
              </p>
            </div>
          )}

          {field.help && (
            <p className="border-t border-line px-3.5 py-3 text-[12.5px] leading-relaxed text-muted">{field.help}</p>
          )}

          {/* The full option set with what each one MEANS — inline the form can only ever show
              the meaning of the option currently selected. */}
          {(field.choices?.length || field.options?.length) && (
            <div className="border-t border-line px-3.5 py-3">
              <Eyebrow className="mb-2">Accepted values</Eyebrow>
              <ul className="space-y-1.5">
                {normalizeChoices(field).map((c) => {
                  const sel = String(value ?? "") === c.value;
                  const { detail, risk } = c;
                  return (
                    <li key={c.value} className="flex gap-2">
                      <code
                        className={cn(
                          "shrink-0 font-mono text-[11.5px]",
                          sel ? "text-accent" : "text-muted",
                        )}
                      >
                        {c.value}
                      </code>
                      {(detail || risk) && (
                        <span className={cn("min-w-0 text-[11.5px]", risk ? "text-warn" : "text-faint")}>
                          {risk || detail}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {file && (
            <div className="border-t border-line px-3.5 py-2.5">
              <span className="font-mono text-[11px] text-faint">written to {file}</span>
            </div>
          )}
          <Popover.Arrow className="fill-[var(--ozp-line)]" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3.5 py-2">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-fg">{value}</dd>
    </div>
  );
}
